/**
 * Static configuration for the "Elevator Installer Intake Checklist"
 * (Automated Dry-Touch Robot Call System - Pre-Installation & Commissioning Record).
 *
 * The appointment detail page renders these sections generically and stores every
 * value in the appointment's `checklist_data` JSONB, keyed by field id. Because the
 * form is fixed (from the source PDF), the schema lives here in the frontend rather
 * than in the database.
 *
 * Value storage by field type:
 *   - text / textarea  -> string
 *   - checkbox         -> boolean
 *   - passfail         -> 'Pass' | 'Fail' | 'N/A' | ''
 *   - a field with `notes: true` also stores a string at `${id}_notes`
 *   - table            -> Array<Record<columnKey, string | boolean>>
 */

export type SimpleFieldType = 'text' | 'textarea' | 'checkbox' | 'passfail';

export interface SimpleField {
  id: string;
  label: string;
  type: SimpleFieldType;
  /** For checkbox/passfail: also render an inline notes input stored at `${id}_notes`. */
  notes?: boolean;
}

export interface TableColumn {
  key: string;
  label: string;
  type: 'text' | 'checkbox' | 'passfail' | 'signature';
}

export interface TableField {
  id: string;
  label: string;
  type: 'table';
  columns: TableColumn[];
  /** Fixed row labels (first column). If omitted, rows are user-addable. */
  fixedRows?: string[];
  firstColLabel?: string;
  addRowLabel?: string;
}

export type FormField = SimpleField | TableField;

export interface FormGroup {
  title?: string;
  layout?: 'grid' | 'checks' | 'stack';
  fields: FormField[];
}

export interface FormSection {
  id: string;
  number?: string;
  title: string;
  description?: string;
  groups: FormGroup[];
}

export function isTableField(f: FormField): f is TableField {
  return f.type === 'table';
}

const PASS_FAIL_NA = ['Pass', 'Fail', 'N/A'] as const;
export const PASS_FAIL_OPTIONS: readonly string[] = PASS_FAIL_NA;

export const ELEVATOR_INTAKE_FORM: FormSection[] = [
  {
    id: 'header',
    title: 'Work Order & Job Site',
    groups: [
      {
        layout: 'grid',
        fields: [
          { id: 'work_order_number', label: 'Work Order #', type: 'text' },
          { id: 'work_order_date', label: 'Date', type: 'text' },
          { id: 'job_site_name', label: 'Job Site Name', type: 'text' },
          { id: 'floor_location', label: 'Floor / Location', type: 'text' },
          { id: 'lead_installer', label: 'Lead Installer', type: 'text' },
          { id: 'company_contractor', label: 'Company / Contractor', type: 'text' },
          { id: 'supervisor_pm', label: 'Supervisor / PM', type: 'text' },
          { id: 'header_contact_phone', label: 'Contact Phone', type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'site_details',
    number: '01',
    title: 'Site Details & Environment',
    groups: [
      {
        layout: 'grid',
        fields: [
          { id: 'building_name_address', label: 'Building Name & Address', type: 'text' },
          { id: 'city_state', label: 'City / State', type: 'text' },
          { id: 'zip_code', label: 'ZIP Code', type: 'text' },
          { id: 'building_type', label: 'Building Type', type: 'text' },
          { id: 'number_of_floors', label: 'Number of Floors', type: 'text' },
          { id: 'elevator_shaft_access', label: 'Elevator Shaft Access', type: 'text' },
          { id: 'machine_room_location', label: 'Machine Room Location', type: 'text' },
          { id: 'power_source_available', label: 'Power Source Available', type: 'text' },
          { id: 'voltage_phase', label: 'Voltage / Phase', type: 'text' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'site_condition_notes', label: 'Site Condition Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'elevator_specs',
    number: '02',
    title: 'Elevator Specifications',
    groups: [
      {
        layout: 'grid',
        fields: [
          { id: 'elevator_make', label: 'Elevator Make', type: 'text' },
          { id: 'elevator_model', label: 'Model', type: 'text' },
          { id: 'elevator_serial', label: 'Serial #', type: 'text' },
          { id: 'year_installation', label: 'Year of Installation', type: 'text' },
          { id: 'rated_speed_fpm', label: 'Rated Speed (FPM)', type: 'text' },
          { id: 'rated_capacity_lbs', label: 'Rated Capacity (lbs)', type: 'text' },
          { id: 'drive_type', label: 'Drive Type', type: 'text' },
          { id: 'number_of_stops', label: 'Number of Stops', type: 'text' },
          { id: 'door_type', label: 'Door Type', type: 'text' },
        ],
      },
      {
        title: 'Elevator Condition Assessment',
        layout: 'checks',
        fields: [
          { id: 'cond_working_prior', label: 'Elevator in working order prior to install', type: 'checkbox' },
          { id: 'cond_door_sensors', label: 'Door sensors functional', type: 'checkbox' },
          { id: 'cond_cab_interior', label: 'Cab interior in good condition', type: 'checkbox' },
          { id: 'cond_intercom', label: 'Intercom / phone system working', type: 'checkbox' },
          { id: 'cond_existing_fixtures', label: 'Existing fixtures documented', type: 'checkbox' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'elevator_spec_notes', label: 'Elevator Spec Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'controller',
    number: '03',
    title: 'Controller Type & Compatibility',
    groups: [
      {
        layout: 'grid',
        fields: [
          { id: 'controller_manufacturer', label: 'Controller Manufacturer', type: 'text' },
          { id: 'controller_model', label: 'Controller Model', type: 'text' },
          { id: 'controller_firmware', label: 'Controller Firmware Version', type: 'text' },
          { id: 'controller_serial_panel_id', label: 'Serial / Panel ID', type: 'text' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'controller_location', label: 'Controller Location (describe)', type: 'textarea' }] },
      {
        title: 'Compatibility Verification',
        layout: 'stack',
        fields: [
          { id: 'compat_protocol_verified', label: 'Controller communication protocol verified with robot system', type: 'passfail' },
          { id: 'compat_voltage_levels', label: 'Voltage levels compatible (robot dry-contact output)', type: 'passfail' },
          { id: 'compat_floor_call_mapping', label: 'Floor call mapping confirmed in controller', type: 'passfail' },
          { id: 'compat_documentation', label: 'Controller documentation / wiring diagram available', type: 'passfail' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'controller_notes', label: 'Controller Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'robot_integration',
    number: '04',
    title: 'Robot Integration Requirements',
    groups: [
      {
        layout: 'grid',
        fields: [
          { id: 'robot_make_model', label: 'Robot Make / Model', type: 'text' },
          { id: 'robot_firmware', label: 'Firmware Version', type: 'text' },
          { id: 'number_of_robots', label: 'Number of Robots on Site', type: 'text' },
          { id: 'robot_dispatch_system', label: 'Robot Dispatch System', type: 'text' },
          { id: 'api_middleware_platform', label: 'API / Middleware Platform Used', type: 'text' },
        ],
      },
      {
        title: 'Integration Method',
        layout: 'checks',
        fields: [
          { id: 'integ_dry_contact', label: 'Direct dry-contact relay board', type: 'checkbox' },
          { id: 'integ_ip_module', label: 'IP-based elevator integration module', type: 'checkbox' },
          { id: 'integ_cloud_api', label: 'Cloud dispatch API', type: 'checkbox' },
          { id: 'integ_onprem_middleware', label: 'On-premise middleware server', type: 'checkbox' },
          { id: 'integ_thirdparty_box', label: 'Third-party integration box', type: 'checkbox' },
          { id: 'integ_other', label: 'Other (specify in notes)', type: 'checkbox' },
        ],
      },
      {
        title: 'Call Requirements per Robot',
        layout: 'checks',
        fields: [
          { id: 'call_hall', label: 'Hall call (up/down per floor)', type: 'checkbox' },
          { id: 'call_car', label: 'Car call (destination floor)', type: 'checkbox' },
          { id: 'call_door_hold', label: 'Door hold capability', type: 'checkbox' },
          { id: 'call_priority', label: 'Priority / express mode', type: 'checkbox' },
          { id: 'call_multi_queue', label: 'Multi-robot queuing', type: 'checkbox' },
          { id: 'call_return_home', label: 'Return-to-floor (home) function', type: 'checkbox' },
        ],
      },
      {
        layout: 'grid',
        fields: [
          { id: 'floors_served_by_robot', label: 'Number of Floors Served by Robot', type: 'text' },
          { id: 'floor_id_mapping_scheme', label: 'Floor ID Mapping Scheme (e.g., B1=0, L1=1)', type: 'text' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'integration_notes', label: 'Integration Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'wiring',
    number: '05',
    title: 'Wiring & Electrical',
    groups: [
      {
        title: 'Dry-Touch / Dry-Contact Wiring',
        layout: 'stack',
        fields: [
          { id: 'wiring_relay_mounted', label: 'Relay board mounted and secured in controller panel', type: 'checkbox', notes: true },
          { id: 'wiring_hall_call_run', label: 'Hall call wiring run for each floor (up/down)', type: 'checkbox', notes: true },
          { id: 'wiring_car_call_run', label: 'Car call wiring run for each destination floor', type: 'checkbox', notes: true },
          { id: 'wiring_door_hold', label: 'Door hold relay wired and tested', type: 'checkbox', notes: true },
          { id: 'wiring_estop_bypass_not_wired', label: 'Emergency stop bypass relay NOT wired (safety compliance)', type: 'checkbox', notes: true },
          { id: 'wiring_ground_shared', label: 'Ground/common shared correctly between relay and controller', type: 'checkbox', notes: true },
        ],
      },
      {
        title: 'Cable Management',
        layout: 'checks',
        fields: [
          { id: 'cable_labeled', label: 'All cables labeled at both ends', type: 'checkbox' },
          { id: 'cable_routing_avoids_moving', label: 'Cable routing avoids moving parts', type: 'checkbox' },
          { id: 'cable_conduit_used', label: 'Conduit used in shaft / hoistway', type: 'checkbox' },
          { id: 'cable_ties_installed', label: 'Cable ties / restraints installed', type: 'checkbox' },
          { id: 'cable_bend_radius', label: 'Minimum bend radius maintained', type: 'checkbox' },
          { id: 'cable_strain_relief', label: 'Strain relief at termination points', type: 'checkbox' },
        ],
      },
      {
        title: 'Electrical Measurements',
        layout: 'grid',
        fields: [
          { id: 'elec_control_voltage', label: 'Control Voltage (VAC/VDC)', type: 'text' },
          { id: 'elec_relay_contact_rating', label: 'Relay Contact Rating', type: 'text' },
          { id: 'elec_measured_voltage', label: 'Measured Voltage', type: 'text' },
          { id: 'elec_continuity_tested', label: 'Continuity Tested?', type: 'text' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'wiring_notes', label: 'Wiring Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'mounting',
    number: '06',
    title: 'Mounting & Physical Installation',
    groups: [
      {
        title: 'Robot Landing Station / Docking Area',
        layout: 'stack',
        fields: [
          { id: 'mount_landing_zone', label: 'Landing zone dimensions confirmed (min. robot clearance)', type: 'checkbox', notes: true },
          { id: 'mount_floor_markings', label: 'Floor markings / guides installed', type: 'checkbox', notes: true },
          { id: 'mount_charging_station', label: 'Charging station position verified (if applicable)', type: 'checkbox', notes: true },
          { id: 'mount_docking_alignment', label: 'Docking alignment to elevator door threshold confirmed', type: 'checkbox', notes: true },
          { id: 'mount_no_obstructions', label: 'No obstructions in robot path (zero-turn radius)', type: 'checkbox', notes: true },
        ],
      },
      {
        title: 'Elevator Interior Mounting',
        layout: 'stack',
        fields: [
          { id: 'mount_relay_bracket', label: 'Relay / call board bracket mounted per spec', type: 'checkbox', notes: true },
          { id: 'mount_antenna_beacon', label: 'Antenna or beacon installed (if wireless system)', type: 'checkbox', notes: true },
          { id: 'mount_no_interference', label: 'No interference with existing cab fixtures', type: 'checkbox', notes: true },
          { id: 'mount_weight_tolerance', label: 'Weight of added equipment within cab tolerance', type: 'checkbox', notes: true },
        ],
      },
      {
        layout: 'grid',
        fields: [
          { id: 'relay_board_mounting_location', label: 'Relay Board Mounting Location', type: 'text' },
          { id: 'fastener_type_used', label: 'Fastener Type Used', type: 'text' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'mounting_notes', label: 'Mounting Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'safety',
    number: '07',
    title: 'Safety Checks',
    groups: [
      {
        title: 'Robot Safety Integration',
        layout: 'checks',
        fields: [
          { id: 'rsafe_no_activate_door_close', label: 'Robot cannot activate elevator during door-close sequence', type: 'checkbox' },
          { id: 'rsafe_estop_command_tested', label: 'Emergency stop command from robot to elevator tested', type: 'checkbox' },
          { id: 'rsafe_hold_door_release', label: 'Robot hold-door command releases correctly after timeout', type: 'checkbox' },
          { id: 'rsafe_obstacle_detection', label: 'Obstacle detection prevents robot from entering moving cab', type: 'checkbox' },
          { id: 'rsafe_no_obstruct_edge', label: 'Robot does not obstruct door safety edge at any point', type: 'checkbox' },
          { id: 'rsafe_failsafe_disconnect', label: 'Fail-safe: elevator returns to normal if robot disconnects', type: 'checkbox' },
          { id: 'rsafe_cyber_restricted', label: 'Cybersecurity: robot API access restricted / credentialed', type: 'checkbox' },
        ],
      },
      {
        title: 'Code Compliance',
        layout: 'checks',
        fields: [
          { id: 'code_asme', label: 'ASME A17.1 / CSA B44 compliance confirmed', type: 'checkbox' },
          { id: 'code_ada', label: 'ADA accessibility maintained post-install', type: 'checkbox' },
          { id: 'code_ahj_permit', label: 'Local AHJ permit obtained', type: 'checkbox' },
          { id: 'code_osha_logs', label: 'OSHA lockout/tagout logs completed', type: 'checkbox' },
          { id: 'code_fire_clearances', label: 'Fire code clearances maintained', type: 'checkbox' },
          { id: 'code_ul_listing', label: 'UL listing of relay components verified', type: 'checkbox' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'safety_check_notes', label: 'Safety Check Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'functional_testing',
    number: '08',
    title: 'Functional Testing',
    groups: [
      {
        title: 'Hall Call Tests (per floor)',
        layout: 'stack',
        fields: [
          {
            id: 'hall_call_tests',
            label: 'Hall Call Tests',
            type: 'table',
            addRowLabel: 'Add floor',
            columns: [
              { key: 'floor', label: 'Floor / Level', type: 'text' },
              { key: 'up_call', label: 'Up Call OK', type: 'checkbox' },
              { key: 'down_call', label: 'Down Call OK', type: 'checkbox' },
              { key: 'car_call', label: 'Car Call OK', type: 'checkbox' },
              { key: 'door_hold', label: 'Door Hold OK', type: 'checkbox' },
              { key: 'travel_time', label: 'Travel Time', type: 'text' },
              { key: 'pass_fail', label: 'Pass/Fail', type: 'passfail' },
            ],
          },
        ],
      },
      {
        title: 'End-to-End Robot Dispatch Tests',
        layout: 'stack',
        fields: [
          { id: 'e2e_call_arrives', label: 'Robot calls elevator from floor - arrives, doors open', type: 'passfail' },
          { id: 'e2e_boards_travels', label: 'Robot boards, presses car call, cab travels to destination', type: 'passfail' },
          { id: 'e2e_exits_closes', label: 'Robot exits cab, doors close normally', type: 'passfail' },
          { id: 'e2e_multi_floor', label: 'Multiple floor call sequence (3-stop cycle)', type: 'passfail' },
          { id: 'e2e_peak_traffic', label: 'Peak traffic / concurrent call test (2+ robots)', type: 'passfail' },
          { id: 'e2e_door_hold_duration', label: 'Door hold: robot holds door for specified duration', type: 'passfail' },
          { id: 'e2e_door_hold_timeout', label: 'Door hold timeout: door closes after max hold time', type: 'passfail' },
          { id: 'e2e_emergency_fire', label: 'Emergency call: elevator ignores robot during fire service', type: 'passfail' },
          { id: 'e2e_recovery_comms', label: 'System recovery after robot comms failure', type: 'passfail' },
          { id: 'e2e_powerloss', label: 'Power-loss test: elevator defaults to normal operation', type: 'passfail' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'test_results_observations', label: 'Test Results & Observations', type: 'textarea' }] },
    ],
  },
  {
    id: 'system_config',
    number: '09',
    title: 'System Configuration & Settings',
    groups: [
      {
        title: 'Robot System Settings Recorded',
        layout: 'grid',
        fields: [
          { id: 'cfg_elevator_id', label: 'Elevator ID in Robot System', type: 'text' },
          { id: 'cfg_api_endpoint_ip', label: 'API Endpoint / IP', type: 'text' },
          { id: 'cfg_port', label: 'Port', type: 'text' },
          { id: 'cfg_auth_token', label: 'Auth Token/Key', type: 'text' },
          { id: 'cfg_max_hold_door', label: 'Max Hold-Door Time (sec)', type: 'text' },
          { id: 'cfg_call_timeout', label: 'Call Timeout (sec)', type: 'text' },
        ],
      },
      {
        layout: 'checks',
        fields: [
          { id: 'cfg_settings_backed_up', label: 'Settings backed up to cloud / server', type: 'checkbox' },
          { id: 'cfg_config_document_provided', label: 'Config document provided to client', type: 'checkbox' },
          { id: 'cfg_remote_access_documented', label: 'Remote access credentials documented', type: 'checkbox' },
          { id: 'cfg_monitoring_configured', label: 'Monitoring / alerting configured', type: 'checkbox' },
        ],
      },
      {
        title: 'Elevator Controller Settings Recorded',
        layout: 'grid',
        fields: [
          { id: 'cfg_priority_mode', label: 'Priority Mode Setting', type: 'text' },
          { id: 'cfg_robot_car_call_code', label: 'Robot Car Call Code', type: 'text' },
          { id: 'cfg_robot_hall_call_code', label: 'Robot Hall Call Code', type: 'text' },
        ],
      },
      {
        layout: 'checks',
        fields: [
          { id: 'cfg_controller_saved', label: 'Controller settings saved / exported', type: 'checkbox' },
          { id: 'cfg_default_settings_documented', label: 'Default settings documented (pre-install)', type: 'checkbox' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'configuration_notes', label: 'Configuration Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'handover',
    number: '10',
    title: 'Handover, Training & Client Acceptance',
    groups: [
      {
        title: 'Training Completed',
        layout: 'checks',
        fields: [
          { id: 'train_normal_operation', label: 'Client staff trained on robot / elevator normal operation', type: 'checkbox' },
          { id: 'train_emergency_override', label: 'Emergency override procedure demonstrated', type: 'checkbox' },
          { id: 'train_maintenance_intervals', label: 'Maintenance intervals explained', type: 'checkbox' },
          { id: 'train_support_process', label: 'Support contact process confirmed with client', type: 'checkbox' },
        ],
      },
      { layout: 'stack', fields: [{ id: 'handover_notes', label: 'Handover Notes', type: 'textarea' }] },
    ],
  },
  {
    id: 'signoff',
    number: '11',
    title: 'Sign-Off & Certification',
    description:
      'We, the undersigned, certify that the automated dry-touch robot call elevator installation described in this intake form has been completed in accordance with applicable codes, manufacturer specifications, and the requirements of the project. All safety checks, functional tests, and client training have been performed and documented herein.',
    groups: [
      {
        layout: 'stack',
        fields: [
          {
            id: 'signoff_table',
            label: 'Sign-Off',
            type: 'table',
            firstColLabel: 'Role',
            fixedRows: [
              'Lead Installer',
              'Elevator Mechanic / Inspector',
              'Robot Systems Technician',
              'Site Supervisor / PM',
              'Client Representative',
            ],
            columns: [
              { key: 'printed_name', label: 'Printed Name', type: 'text' },
              { key: 'signature', label: 'Signature', type: 'signature' },
              { key: 'date', label: 'Date', type: 'text' },
            ],
          },
        ],
      },
      { layout: 'stack', fields: [{ id: 'additional_remarks', label: 'Additional Remarks / Punch List Items', type: 'textarea' }] },
    ],
  },
];
